/**
 * HorizontalBarChart — ECharts horizontal bar chart for rankings.
 *
 * Props:
 * - data: Array<{ name: string, value: number }>
 * - height: number
 * - color: string
 * - theme: 'light' | 'dark'
 * - barWidth: number
 */

import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

export default function HorizontalBarChart({
  data = [],
  height = 260,
  color = '#2d3a52',
  theme = 'light',
  barWidth = 16,
}) {
  const isDark = theme === 'dark';
  const sorted = useMemo(() => [...data].sort((a, b) => a.value - b.value), [data]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (p) => `${p[0].name}<br/>${p[0].value.toLocaleString()}`,
      },
      grid: { left: 90, right: 30, top: 10, bottom: 10 },
      xAxis: {
        type: 'value',
        axisLabel: {
          color: isDark ? 'rgba(228,232,237,0.5)' : '#6b7280',
          fontSize: 10,
          formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v),
        },
        splitLine: { lineStyle: { color: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6' } },
      },
      yAxis: {
        type: 'category',
        data: sorted.map((d) => d.name),
        axisLabel: {
          color: isDark ? 'rgba(228,232,237,0.6)' : '#374151',
          fontSize: 11,
          width: 80,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb' } },
      },
      series: [
        {
          type: 'bar',
          data: sorted.map((d, i) => ({
            value: d.value,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  { offset: 0, color: `${color}88` },
                  { offset: 1, color },
                ],
              },
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth,
          emphasis: {
            itemStyle: { shadowBlur: 4, shadowColor: 'rgba(0,0,0,0.2)' },
          },
        },
      ],
    }),
    [sorted, color, barWidth, isDark]
  );

  if (!data.length) return null;

  return (
    <ReactECharts
      option={option}
      style={{ width: '100%', height }}
      opts={{ renderer: 'canvas' }}
      notMerge
    />
  );
}
